import { useCallback, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GripVertical, Loader2, Trash2, Upload } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ManagedImage {
  id: number;
  url: string;
  filename: string;
  content_type: string;
  byte_size: number;
  position: number;
}

interface SortableImageUploaderProps {
  title?: string;
  description?: string;
  images: ManagedImage[];
  uploadPath: string;
  reorderPath: string;
  deletePath: (id: number) => string;
}

export function SortableImageUploader({
  title = "الصور",
  description = "حمّل الصور وقم بترتيبها بالسحب والإفلات",
  images,
  uploadPath,
  reorderPath,
  deletePath,
}: SortableImageUploaderProps) {
  const [items, setItems] = useState<ManagedImage[]>(() =>
    [...images].sort((a, b) => a.position - b.position)
  );
  const [uploading, setUploading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const dragSourceId = useRef<number | null>(null);
  const csrfToken = useMemo(
    () => (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content,
    []
  );

  const persistOrder = useCallback(
    async (ordered: ManagedImage[]) => {
      setSavingOrder(true);
      const orderedIds = ordered.map((image) => image.id);
      await fetch(reorderPath, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: JSON.stringify({ image_ids: orderedIds }),
      }).finally(() => setSavingOrder(false));
    },
    [csrfToken, reorderPath]
  );

  const handleReorder = useCallback(
    (overId: number) => {
      const sourceId = dragSourceId.current;
      if (!sourceId || sourceId === overId) return;

      const currentIndex = items.findIndex((item) => item.id === sourceId);
      const overIndex = items.findIndex((item) => item.id === overId);
      if (currentIndex === -1 || overIndex === -1) return;

      const updated = [...items];
      const [moved] = updated.splice(currentIndex, 1);
      updated.splice(overIndex, 0, moved);

      const reindexed = updated.map((item, index) => ({ ...item, position: index + 1 }));
      setItems(reindexed);
      persistOrder(reindexed);
    },
    [items, persistOrder]
  );

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);

    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("image[file]", file);
        formData.append("image[media_kind]", file.type.startsWith("video/") ? "video" : "image");

        const response = await fetch(uploadPath, {
          method: "POST",
          headers: {
            Accept: "application/json",
            ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
          },
          body: formData,
        });

        if (!response.ok) continue;
        const payload = await response.json();
        const image = payload.data as ManagedImage;
        setItems((prev) => {
          const ordered = [...prev, image].sort((a, b) => a.position - b.position);
          return ordered.map((item, index) => ({ ...item, position: index + 1 }));
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const response = await fetch(deletePath(id), {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      },
    });

    if (response.ok) {
      const updated = items.filter((item) => item.id !== id);
      const reindexed = updated.map((item, index) => ({ ...item, position: index + 1 }));
      setItems(reindexed);
      persistOrder(reindexed);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <label
          className={cn(
            "flex h-32 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-muted-foreground/30 bg-muted/40 transition hover:border-primary/70 hover:bg-muted/60",
            uploading && "opacity-70"
          )}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            handleFiles(event.dataTransfer.files);
          }}
        >
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(event) => handleFiles(event.target.files)}
          />
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {uploading ? "يتم رفع الوسائط..." : "اسحب الصور أو الفيديوهات هنا أو اضغط للاختيار"}
          </div>
          <p className="text-xs text-muted-foreground">الحد الأقصى: صور وفيديوهات متعددة</p>
        </label>

        {items.length === 0 ? (
          <div className="rounded-md border border-dashed border-muted-foreground/30 bg-muted/40 p-6 text-center text-sm text-muted-foreground">
            لم يتم رفع أي وسائط بعد
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((image) => (
              <div
                key={image.id}
                draggable
                onDragStart={() => {
                  dragSourceId.current = image.id;
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  handleReorder(image.id);
                }}
                onDragEnd={() => {
                  dragSourceId.current = null;
                }}
                className="group relative flex w-full flex-col gap-3 rounded-lg border bg-background p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-medium text-foreground line-clamp-1">
                      {image.filename}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(image.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="relative h-40 w-full overflow-hidden rounded-md bg-muted">
                  {image.content_type.startsWith("video/") ? (
                    <video
                      src={image.url}
                      controls
                      preload="metadata"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <img
                      src={image.url}
                      alt={image.filename}
                      className="h-full w-full object-cover"
                    />
                  )}
                  {savingOrder && (
                    <div className="absolute inset-0 bg-background/50">
                      <Loader2 className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 animate-spin" />
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>الترتيب: {image.position}</span>
                  <span>
                    {(image.byte_size / 1024 / 1024).toFixed(1)} MB · {image.content_type}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
